<script setup lang="ts">
import { useBaseStore } from "@/stores/base";
import { computed, toRefs } from "vue";

interface BreadcrumbItem {
	title: string;
	disabled: boolean;
	to: string;
}

interface InternalItem extends BreadcrumbItem {
	fullTitle: string;
}

const props = withDefaults(
	defineProps<{
		breadcrumbs: BreadcrumbItem[];
		isUser?: boolean;
		inline?: boolean;
	}>(),
	{
		isUser: false,
		inline: false,
	}
);

const { breadcrumbs, isUser } = toRefs(props);

const store = useBaseStore();

const MAX = 20;
const truncate = (title: string) =>
	title.length > MAX ? title.slice(0, MAX) + "…" : title;

const toInternal = (item: BreadcrumbItem): InternalItem => ({
	...item,
	fullTitle: item.title,
	title: truncate(item.title),
});

const items = computed<InternalItem[]>(() => {
	const firstTitle = isUser.value
		? store.currentUser?.name || ""
		: "Home";
	const first: InternalItem = {
		title: truncate(firstTitle),
		fullTitle: firstTitle,
		disabled: false,
		to: "/home",
	};
	return [first, ...breadcrumbs.value.map(toInternal)];
});
</script>

<template>
	<teleport defer to="#breadcrumbs-header" :disabled="inline">
		<v-breadcrumbs v-if="items.length" :items="items" class="ma-0 pa-0">
			<template v-slot:divider>
				<v-icon icon="mdi-chevron-right" class="mx-n2" />
			</template>
			<template v-slot:title="{ item }">
				<v-tooltip
					v-if="item.fullTitle !== item.title"
					:text="item.fullTitle"
					location="bottom"
				>
					<template v-slot:activator="{ props: tooltipProps }">
						<v-breadcrumbs-item
							:to="item.to"
							:disabled="item.disabled"
							class="ma-0 pa-0"
							v-bind="tooltipProps"
						>
							<span class="text-h6 font-weight-bold">{{ item.title }}</span>
						</v-breadcrumbs-item>
					</template>
				</v-tooltip>

				<v-breadcrumbs-item
					v-else
					:to="item.to"
					:disabled="item.disabled"
					class="ma-0 pa-0"
				>
					<span class="text-h6 font-weight-bold">{{ item.title }}</span>
				</v-breadcrumbs-item>
			</template>
		</v-breadcrumbs>
	</teleport>
</template>
